import React, { useState, useEffect, useContext, useCallback,  } from "react";
import moment from 'moment';
import { Button, Table, Space, Dropdown, notification, Input, Select, DatePicker } from "antd";
import { EditOutlined, DownOutlined, SearchOutlined} from "@ant-design/icons";
import axios from "axios";
import GlobalContext from "../utils/GlobalContext";
import { v4 as uuidv4 } from "uuid";
import LoadingModal from "../utils/LoadingModal";
import formatTimestamp from "../utils/FormatTimestamp";
import { Link } from "react-router-dom";
import AgreementDecisionFollowupForm from "../components/Forms/AgreementDecisionFollowupForm";
import AgreementActionFollowupForm from "../components/Forms/AgreementActionFollowupForm";

const PAGE_SIZE = 15;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FollowupTable = () => {
  const globals = useContext(GlobalContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
  });
  const [searchText, setSearchText] = useState("");
  const [classificationId, setClassificationId] = useState(1);
  const [dateRange, setDateRange] = useState("");
  const [showUpdateAgreementDecision, setShowUpdateAgreementDecision] = useState(false);
  const [showUpdateAgreementAction, setShowUpdateAgreementAction] = useState(false);
  const [currentAgreementId, setCurrentAgreementId] = useState(null);
  const [currentMeetingPointDetailName, setCurrentMeetingPointDetailName] = useState(null);
  const [currentMeetingPointDetailId, setCurrentMeetingPointDetailId] = useState(null);
  const [nextMeetingStartDate, setNextMeetingStartDate] = useState(null);

  const columns = [
    {
      title: "Nombre del punto",
      dataIndex: "pointName",
      key: "pointName",
      render: (_, record) => {
        if (record.parentPointName !== null)
          return (
            <p>{record.parentPointName} / {record.pointName}</p>
          )
        else
          return (
            <p>{record.pointName}</p>
          )
      }
    },
    {
      title: "Nombre de la reunión",
      dataIndex: ["meeting", "meeting_name"],
      key: "meeting_type",
      render: (_, record) => {
        return (
          <Link to={`/meeting/${record.meeting.id}/execute`}>{record.meeting.meeting_name}</Link>
        )
      }
    },
    {
      title: "Fecha de revisión",
      dataIndex: "nextMeetingStartDate",
      key: "nextMeetingStartDate",
      render: (_, record) => {
        if (record.agreement.nextMeetingStartDate !== null)
          return (
            formatTimestamp(record.agreement.nextMeetingStartDate)
          )
        else
          return (
            <p>No hay siguiente reunión</p>
          )
      }
    },
    {
      title: (classificationId === 1) ? "Responsable de seguimiento de éxito" : "Responsable de cumplimiento",
      dataIndex: ["agreement", "responsible"],
      key: "department",
    },
    {
      title: "Opciones",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Space size="middle">
          <Dropdown
            trigger={["click"]}
            menu={{
              items:[
                {
                  key: '1',
                  label: 'Editar',
                  icon:<EditOutlined />,
                  onClick: () => handleActionClick("edit", record)
                },
              ],
            }}
          >
            <Button icon={<DownOutlined />} shape="circle" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  const fetchData = useCallback(
    async (newPagination, search, classification_id, startDate, endDate) => {
      let page = 1;
      let pageSize = PAGE_SIZE;
      if (newPagination) {
        page = newPagination.current;
        pageSize = newPagination.pageSize;
      }
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 100));
        let params = {
            page,
            limit: pageSize,
            search: search,
            classificationId: classification_id,
            nextMeetingStartDate_from: (startDate) ? moment(startDate).format("YYYY-MM-DD") : null,
            nextMeetingStartDate_to: (endDate) ? moment(endDate).format("YYYY-MM-DD") : null,
          }
        const response = await axios.get(
          `${globals.SERVICE_API_DB}/v1/consult-meeting-point-detail-open-last-meeting`,
          {
            headers: {
              "x-token": globals.token,
            },
            params,
          }
        );
        if (response.data.status !== 0){
          setLoading(false);
          return;
        }
        setData(response?.data?.data?.rows);
        if (newPagination) {
          setPagination({
            ...newPagination,
            total: response?.data?.data?.count,
          });
        } else {
          setPagination({
            ...pagination,
            total: response?.data?.data?.count,
          }
          );
        }
        notification.success({
          message: "Información obtenida exitosamente!",
          placement: "bottomRight",
          duration: 2,
        });
      } catch (error) {
        console.log("Error fetching data:", error.response);
        if (error?.response?.data?.status !== -3) {
          console.error("Error fetching data:", error.response);
          notification.error({
            message: "Error obteniendo información!",
            placement: "bottomRight",
            duration: 2,
          });
        }
        else{
          notification.warning({
            message: "No hay reuniones disponibles!",
            placement: "bottomRight",
            duration: 2,
          });
        
        }
      }
      setLoading(false);
    },
    // eslint-disable-next-line
    [globals.SERVICE_SERVICE_API_DB]
  );

  useEffect(() => {
    fetchData(null, null, 1, null, null);
  }, [fetchData]);

  const handleActionClick = async (key, record) => {
    if (key === "edit") {
      if (classificationId === 1){
        setShowUpdateAgreementDecision(true);
      }
      else{
        setShowUpdateAgreementAction(true);
      }
      setCurrentAgreementId((record.agreement.nextAgreementId)? record.agreement.nextAgreementId : record.agreement.id);
      setCurrentMeetingPointDetailName(record.pointName);
      setCurrentMeetingPointDetailId(record.id);
      setNextMeetingStartDate(record?.agreement.nextMeetingStartDate);
    }
  };

  async function handleTableChange(newPagination) {
    fetchData(newPagination, searchText, classificationId, dateRange?.start, dateRange?.end);
  }

  const handleRefresh = () => {
    fetchData(null, searchText, classificationId, dateRange?.start, dateRange?.end);
  };

  const handleSearch = async () => {
    console.log("searchText", searchText); 
    console.log("classificationId", classificationId);
    console.log("dateRange", dateRange);

    fetchData(null, searchText, classificationId, dateRange?.start, dateRange?.end);
  };

  const handleSearchEnterKey = async (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleChangeStatus = async (value) => {
    setClassificationId(value);
    fetchData(null, searchText, value, dateRange?.start, dateRange?.end);
  }
 
  const handleFinish= () => {
    setShowUpdateAgreementDecision(false);
    setShowUpdateAgreementAction(false);
  }

  return (
    <div>
      <div
        style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
      >
        <h2 style={{ marginRight: "16px" }}>Seguimiento de reuniones</h2>
        <div style={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
        <Button
          onClick={handleRefresh}
          disabled={loading}
          style={{ marginRight: "8px" }}
        >
          Refrescar
        </Button>
      </div>
      <Input
        placeholder="Buscar"
        allowClear
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16, width: 200 }}
        prefix={<SearchOutlined />}
        onKeyDown={handleSearchEnterKey}
      />
      <RangePicker
        style={{ marginBottom: 16, width: 200 }}
        onChange={(date, dateString) => {
          if (dateString[0] === "" || dateString[0] === null) {
            setDateRange("");
          }
          else{
            const startTimestamp = new Date(`${dateString[0]}T00:00:00Z`).getTime();
            const endTimestamp = new Date(`${dateString[1]}T23:59:59Z`).getTime();
            setDateRange({start: startTimestamp, end: endTimestamp});
          }
        }}
      />
      <Select
        style={{ width: 200, marginLeft: 8 }}
        value={classificationId}
        onChange={handleChangeStatus}
        placeholder="Estados"
      >
        <Option key={1} value={1}>
          Decisiones
        </Option>
        <Option key={2} value={2}>
          Acciones
        </Option>
      </Select>
      <Button onClick={handleSearch} style={{ marginRight: "8px" }}>
        Buscar
      </Button>
      <div
        className="site-layout-background h-auto"
        style={{
          padding: 10,
          display: "flex",
          flexDirection: "column",
          margin: 0,
          height: "auto",
          minHeight: "350px",
          overflow: "auto",
          backgroundColor: "#ffffff",
        }}
      >
        <Table
          rowKey={(r) => uuidv4()}
          columns={columns}
          dataSource={data}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ y: "max-content" }}
          size="small"
        />
      </div>
      <LoadingModal open={loading} />
      <AgreementDecisionFollowupForm
        open={showUpdateAgreementDecision}
        onFinish={handleFinish}
        onCancel={() => setShowUpdateAgreementDecision(false)}
        currentAgreementId={currentAgreementId}
        currentMeetingPointDetailName={currentMeetingPointDetailName}
        currentMeetingPointDetailId={currentMeetingPointDetailId}
        nextMeetingStartDate={nextMeetingStartDate}
      />
      <AgreementActionFollowupForm
        open={showUpdateAgreementAction}
        onFinish={handleFinish}
        onCancel={() => setShowUpdateAgreementAction(false)}
        currentAgreementId={currentAgreementId}
        currentMeetingPointDetailName={currentMeetingPointDetailName}
        currentMeetingPointDetailId={currentMeetingPointDetailId}
        nextMeetingStartDate={nextMeetingStartDate}
      />
    </div>
  );
};

export default FollowupTable;
